import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { createRouteForm } from "@bca-compose/compose.constants";
import { IRoute } from "@bca-compose/models/route";
import { IStation } from "@bca-compose/models/station";
import * as composeActions from "@bca-compose/store/compose.actions";
import { IState } from "@bca-compose/store/compose.reducer";
import { AutoSuggestService } from "@bca-core/services/auto-suggest.service";
import {
  DialogIdentifier,
  ProductIdentifier,
  TravelerClass,
} from "@bca-shared/models/_enums";
import { Form } from "@bca-shared/models/form";
import { IProduct } from "@bca-shared/models/product";
import { ISuggestion } from "@bca-shared/modules/type-ahead/type-ahead.models";
import { AppActionTypes } from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { select, Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, take } from "rxjs/operators";

@Component({
  selector: "bca-choose-route-dialog",
  templateUrl: "choose-route-dialog.component.html",
  styleUrls: ["choose-route-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChooseRouteDialogComponent
  extends Form<IRoute>
  implements OnInit, OnDestroy
{
  @Output() onRouteSelected = new EventEmitter<IProduct>();

  TravelerClass = TravelerClass;
  showRouteValidationError = false;
  searchStation: (
    searchTerm$: Observable<string>,
  ) => Observable<ISuggestion<IStation>[]>;

  isRouteDialogActive$: Observable<boolean>;
  configuredRoute$: Observable<IRoute>;
  stationsLoading$: Observable<boolean>;
  routeLoading$: Observable<boolean>;
  trainFreePrice$: Observable<number>;
  composeState$:Observable<IState>;


  subscriptions = new Subscription();

  constructor(
    private autoSuggestService: AutoSuggestService,
    private store: Store<IRootState>,
    private formBuilder: FormBuilder,
  ) {
    super(createRouteForm(formBuilder));
    this.initializeObservables();
    this.subscribeToConfiguredRoute();
    this.prefillConfiguredRoute();
  }

  ngOnInit(): void {
    this.initializeRouteDialogStatus();
    this.searchStation = this.autoSuggestService.searchStation.bind(
      this.autoSuggestService,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.store.dispatch(
      this.createAction(new composeActions.SetConfiguredRoute(null)),
    );
    this.closeDialog();
  }

  configureRoute(): void {
    if (!this.formGroup.valid) {
      this.showRouteValidationError = true;
      return;
    }

    this.subscriptions.add(
      this.configuredRoute$.pipe(take(1)).subscribe((configuredRoute) => {
        this.store.dispatch(
          this.createAction(
            new composeActions.ConfigureProduct(configuredRoute),
          ),
        );
        this.onRouteSelected.emit();
        this.closeDialog();
      }),
    );
  }

  closeDialog(): void {
    this.showRouteValidationError = false;
    this.store.dispatch({
      type: AppActionTypes.SetDialogStatus,
      payload: { visible: false, identifier: ProductIdentifier.Traject },
    });
  }

  onFromStationSelected(station: IStation): void {
    this.updateRoute({ from: station });
  }

  onToStationSelected(station: IStation): void {
    this.updateRoute({ to: station });
  }

  hasControlError(controlName: string, errorKey: string): boolean {
    const control = this.formGroup.get(controlName);
    return !!control && control.hasError(errorKey) && (control.dirty || control.touched);
  }

  checkIdenticalError(): string | undefined {
    if (this.formGroup.hasError('identical')) {
      return 'COMPOSE.ROUTE.ERRORS.IDENTICAL';
    }
    return undefined;
  }

  private updateRoute(partialRoute: Partial<IRoute>): void {
    this.store.dispatch(
      this.createAction(
        new composeActions.SetConfiguredRoute({
          ...this.formGroup.value,
          ...partialRoute,
        }),
      ),
    );
  }

  private initializeObservables(): void {
    this.composeState$ = this.store.pipe(select("compose"));

    this.configuredRoute$ = this.composeState$.pipe(
      select((state) => state.configuredRoute),
    );
    this.stationsLoading$ = this.composeState$.pipe(
      select((state) => state.stationsLoading),
    );
    this.routeLoading$ = this.composeState$.pipe(
      select((state) => state.routeLoading),
    );
    this.trainFreePrice$ = this.composeState$.pipe(
      select((state) => state.trainFreePrice),
    );
  }

  private initializeRouteDialogStatus(): void {
    this.isRouteDialogActive$ = this.store.pipe(
      select("app"),
      map((state) => state.dialogStatus[DialogIdentifier.ChooseRoute] || false),
      distinctUntilChanged(),
    );
  }

  private subscribeToConfiguredRoute(): void {
    this.subscriptions.add(
      this.configuredRoute$.subscribe((configuredRoute) => {
        configuredRoute ? this.patchValue(configuredRoute) : this.resetValue();
      }),
    );
  }

  private prefillConfiguredRoute(): void {
    this.subscriptions.add(
      this.store
        .pipe(
          select(({ compose }) => compose.configuredProduct),
          filter(Boolean),
          map((product) => product.route),
          take(1),
        )
        .subscribe((route) =>
          this.store.dispatch(
            this.createAction(new composeActions.SetConfiguredRoute(route)),
          ),
        ),
    );
  }

  private createAction(action: IAction): IAction {
    return createAction(action, ChooseRouteDialogComponent);
  }
}
